var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Box, Button, Grid, IconButton, useMediaQuery, Typography, Alert, Select, MenuItem, FormControl, InputLabel, Skeleton, CircularProgress, } from "@mui/material";
import { PageHeader } from "@ui/components/header/PageHeader";
import FDrawer from "@ui/components/drawer/FDrawer";
import React, { useState, useEffect } from "react";
import { Edit2, InfoCircle, } from "iconsax-react";
import { FMenu } from "@ui/components/menu/FMenu";
import { ForcastAdjustDialog } from "./ForcastAdjustDialog";
import { ProjectTable } from "./ProjectTable";
import { ProjectTableDrawer } from "./ProjectTableDrawer";
import { TreeMenuNew } from "@ui/components/tree-menu/TreeMenuNew";
import { stack2 } from "@ui/components/style/style";
import { Icon } from "@iconify/react";
import { useGetProjectTreeQuery, useGetCombinedMetricsQuery, useSaveProjectMetricMutation, useCreateForecastAdjustmentMutation, } from "../../../features/projects/projectsApi";
import { useRefreshProjectStatusesMutation, } from "../../../features/projectStatuses/projectStatusesApi";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { useAuth } from "../../../auth/useAuth";
import { useGetCompanyMonthConfigsQuery } from "../../../features/companyMonthConfigs/companyMonthConfigsApi";
import { usePermissions } from "../../../context/PermissionContext";
// Utility function to transform flat ProjectTreeNodeDto array to hierarchical TreeNode structure
const transformProjectTreeData = (flatData) => {
    if (!flatData || flatData.length === 0)
        return [];
    // Sort by depth to process parent nodes before children
    const sortedData = [...flatData].sort((a, b) => a.depth - b.depth);
    // Create a map to store nodes by their project number
    const nodeMap = new Map();
    const rootNodes = [];
    // First pass: create all nodes
    sortedData.forEach((item, index) => {
        // Construct tooltip combining project number and description
        const tooltipText = item.projectNumber && item.projectDescription
            ? `${item.projectNumber}: ${item.projectDescription}`
            : item.tooltipPath || "";
        const node = {
            id: item.projectNumber || `node-${index}`,
            label: item.projectDescription || `Project ${item.projectNumber}`,
            percentage: item.afgerondPercentage, // Use exact value from backend without rounding
            isExpandable: false, // Will be determined in second pass
            level: item.depth,
            tooltipPath: tooltipText,
        };
        nodeMap.set(node.id, node);
    });
    // Second pass: build hierarchy based on depth and sequential order
    sortedData.forEach((item, index) => {
        const node = nodeMap.get(item.projectNumber || "");
        if (!node)
            return;
        // If this is a root node (depth 0), add to root nodes
        if (item.depth === 0) {
            rootNodes.push(node);
            // Find all direct children (depth 1) and add them
            const directChildren = sortedData
                .filter((child) => child.depth === 1)
                .map((child) => nodeMap.get(child.projectNumber || ""))
                .filter(Boolean);
            if (directChildren.length > 0) {
                node.children = directChildren;
                node.isExpandable = true;
            }
        }
        else {
            // For non-root nodes, try to find children based on project number hierarchy
            const children = sortedData
                .filter((child) => {
                // Child should be one level deeper
                if (child.depth !== item.depth + 1)
                    return false;
                // Child's project number should start with parent's project number
                // Supports both dot-separated (PRB4702.E → PRB4702.E.4)
                // and alphanumeric-suffixed (PRB4702.E → PRB4702.E04) children
                if (!child.projectNumber || !item.projectNumber)
                    return false;
                return child.projectNumber.startsWith(item.projectNumber);
            })
                .map((child) => nodeMap.get(child.projectNumber || ""))
                .filter(Boolean);
            if (children.length > 0) {
                node.children = children;
                node.isExpandable = true;
            }
        }
    });
    return rootNodes;
};
export const ProjectDetails = () => {
    var _a;
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const { account } = useAuth();
    const { canRead, canUpdate, isLoading: isLoadingPermissions, } = usePermissions();
    const [openForecastDialog, setOpenForecastDialog] = useState(false);
    const navigate = useNavigate();
    const [treeDrawerOpen, setTreeDrawerOpen] = useState(false);
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    const [activeNodeId, setActiveNodeId] = useState(undefined);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedChildProject, setSelectedChildProject] = useState(undefined);
    const [showInfoAlert, setShowInfoAlert] = useState(true);
    const [unitType, setUnitType] = useState("full");
    const [menuAnchor, setMenuAnchor] = useState(null);
    const handleMenuOpen = (event) => {
        setMenuAnchor(event.currentTarget);
    };
    const handleMenuClose = () => {
        setMenuAnchor(null);
    };
    const { projectId } = useParams();
    const [searchParams] = useSearchParams();
    // Get parameters from URL query string
    const topProjectNumber = searchParams.get("topProjectNumber") || "";
    const entityName = searchParams.get("entityName") || "";
    const aanneemsom = searchParams.get("aanneemsom") || undefined;
    const companyId = searchParams.get("companyId") || "";
    const hasUpdatePermission = canUpdate("Projects");
    const { data: companyMonthConfigs, isLoading: isLoadingCompanyMonthConfigs, isFetching: isFetchingCompanyMonthConfigs, } = useGetCompanyMonthConfigsQuery(companyId, {
        skip: !companyId,
        refetchOnMountOrArgChange: true,
    });
    const { data: projectTreeData, isLoading: isTreeLoading, error: treeError, refetch: refetchTree, } = useGetProjectTreeQuery({
        topProjectNumber,
        entityName,
        aanneemsom,
        companyId,
    }, {
        skip: !topProjectNumber || !entityName, // Skip the query if parameters are missing
    });
    // Fetch combined metrics for the selected project
    const { data: combinedMetrics, refetch: refetchMetrics } = useGetCombinedMetricsQuery({
        parentProjectNumber: topProjectNumber,
        childProjectNumber: selectedChildProject || topProjectNumber,
        companyId: companyId,
    }, {
        skip: !topProjectNumber || !selectedChildProject || !companyId,
        refetchOnMountOrArgChange: true,
    });
    const [saveProjectMetric, { isLoading: isSaving }] = useSaveProjectMetricMutation();
    const [createForecastAdjustment] = useCreateForecastAdjustmentMutation();
    const [refreshProjectStatuses] = useRefreshProjectStatusesMutation();
    const hasCompanyConfigMonth = Array.isArray(companyMonthConfigs) && companyMonthConfigs.length > 0;
    // POC button: disabled when pocReadOnly is false, enabled when true
    const isPocDisabled = isLoadingCompanyMonthConfigs ||
        isFetchingCompanyMonthConfigs ||
        !hasCompanyConfigMonth ||
        !((_a = companyMonthConfigs[0]) === null || _a === void 0 ? void 0 : _a.pocReadOnly);
    // Forecast/Margin buttons: disabled when no config month
    const isSaveDisabled = isLoadingCompanyMonthConfigs ||
        isFetchingCompanyMonthConfigs ||
        !hasCompanyConfigMonth;
    // Format GUID to proper format (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
    const formatGuid = (guid) => {
        const cleaned = guid.replace(/[-\s]/g, "");
        if (!/^[0-9a-f]{32}$/i.test(cleaned)) {
            return guid;
        }
        return `${cleaned.slice(0, 8)}-${cleaned.slice(8, 12)}-${cleaned.slice(12, 16)}-${cleaned.slice(16, 20)}-${cleaned.slice(20, 32)}`;
    };
    const noAccess = !isLoadingPermissions && !canRead("Projects");
    // Handle saving metrics
    // Only call refreshProjectStatuses on 'Cijfers opslaan', not on forecast aanpassen or dialog open
    const handleSaveMetrics = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (isSaveDisabled) {
            enqueueSnackbar("Opslaan is uitgeschakeld: geen configuratiemaand voor dit bedrijf", { variant: "warning" });
            return;
        }
        if (!combinedMetrics || !topProjectNumber) {
            enqueueSnackbar("Geen metriekgegevens beschikbaar om op te slaan", {
                variant: "warning",
            });
            return;
        }
        if (!companyId) {
            enqueueSnackbar("CompanyId ontbreekt: projectstatus refresh is overgeslagen", {
                variant: "warning",
            });
        }
        try {
            // Get PTD Margin value from additionalMetrics
            const marginMetric = (_a = combinedMetrics.additionalMetrics) === null || _a === void 0 ? void 0 : _a.find((m) => m.metric.toLowerCase().includes("marg") &&
                !m.metric.toLowerCase().includes("%"));
            if (!marginMetric) {
                enqueueSnackbar("Margin metriek niet gevonden", {
                    variant: "warning",
                });
                return;
            }
            // Get PTD POC value from additionalMetrics
            const pocMetric = (_b = combinedMetrics.additionalMetrics) === null || _b === void 0 ? void 0 : _b.find((m) => m.metric.toLowerCase().includes("poc"));
            if (!pocMetric) {
                enqueueSnackbar("POC metriek niet gevonden", {
                    variant: "warning",
                });
                return;
            }
            // Save both Margin and POC
            yield Promise.all([
                saveProjectMetric({
                    parentProjectNumber: topProjectNumber,
                    childProjectNumber: selectedChildProject || topProjectNumber,
                    metricName: "Marg",
                    ptdValue: marginMetric.ptd,
                    savedBy: (account === null || account === void 0 ? void 0 : account.username) || (account === null || account === void 0 ? void 0 : account.name) || "unknown",
                    companyId: companyId,
                }).unwrap(),
                createForecastAdjustment({
                    projectNumber: topProjectNumber,
                    projectId: projectId ? formatGuid(projectId.trim()) : null,
                    childProjectNumber: selectedChildProject || null,
                    pocOverrule: pocMetric.ptd,
                    adjustmentCategory: "AutoSave",
                    comment: null,
                    createdBy: (account === null || account === void 0 ? void 0 : account.username) || (account === null || account === void 0 ? void 0 : account.name) || "unknown",
                    companyId: companyId,
                }).unwrap(),
            ]);
            // Only call refreshProjectStatuses as part of 'Cijfers opslaan', not on forecast aanpassen
            if (companyId) {
                const projectsToRefresh = [
                    {
                        parentProjectNumber: topProjectNumber,
                        childProjectNumber: topProjectNumber,
                        status: "Afgerond",
                    },
                ];
                const child = selectedChildProject || topProjectNumber;
                if (child && child !== topProjectNumber) {
                    projectsToRefresh.push({
                        parentProjectNumber: topProjectNumber,
                        childProjectNumber: child,
                        status: "Afgerond",
                    });
                }
                try {
                    yield refreshProjectStatuses({
                        companyId,
                        triggeredBy: (account === null || account === void 0 ? void 0 : account.username) || (account === null || account === void 0 ? void 0 : account.name) || "unknown",
                        status: "Afgerond",
                        projects: projectsToRefresh,
                    }).unwrap();
                }
                catch (err) {
                    enqueueSnackbar("Projectstatus refresh mislukt", {
                        variant: "warning",
                    });
                }
            }
            enqueueSnackbar("Cijfers succesvol opgeslagen", {
                variant: "success",
            });
        }
        catch (error) {
            enqueueSnackbar("Fout bij het opslaan van cijfers", {
                variant: "error",
            });
        }
    });
    // Derived disabled flags including permission
    const pocButtonDisabled = isPocDisabled || !hasUpdatePermission;
    const saveButtonsDisabled = isSaveDisabled || !hasUpdatePermission;
    // Menu items for mobile
    const menuItems = [
        {
            label: "POC aanpassen",
            disabled: pocButtonDisabled,
            onClick: () => {
                setOpenForecastDialog(true);
                handleMenuClose();
            },
        },
        {
            label: "Forecast aanpassen",
            disabled: saveButtonsDisabled,
            onClick: () => {
                const params = new URLSearchParams(searchParams);
                params.set("childProjectNumber", selectedChildProject || topProjectNumber);
                navigate(`/project/${projectId}/details/forecast?${params.toString()}`, {
                    state: {
                        projectName: selectedTooltipPath || `${topProjectNumber}: ${entityName}`,
                    },
                });
                handleMenuClose();
            },
        },
        {
            label: "Cijfers opslaan",
            disabled: saveButtonsDisabled,
            onClick: () => {
                handleSaveMetrics();
                handleMenuClose();
            },
        },
    ];
    // Handle expand/collapse toggle
    const handleToggle = (nodeId) => {
        const newExpanded = new Set(expandedNodes);
        if (newExpanded.has(nodeId)) {
            newExpanded.delete(nodeId);
        }
        else {
            newExpanded.add(nodeId);
        }
        setExpandedNodes(newExpanded);
    };
    // Handle node selection
    const handleNodeClick = (nodeId) => {
        setActiveNodeId(nodeId);
        setSelectedChildProject(nodeId); // Set the selected child project
        // Open drawer on mobile when a node is selected
        if (isMobile && nodeId) {
            setDrawerOpen(true);
        }
    };
    // Handle drawer close
    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };
    // Close drawer when screen size changes from mobile to desktop
    useEffect(() => {
        if (!isMobile && drawerOpen) {
            setDrawerOpen(false);
        }
    }, [isMobile, drawerOpen]);
    // Helper function to find a node by ID in the tree
    const findNodeById = (nodes, nodeId) => {
        for (const node of nodes) {
            if (node.id === nodeId) {
                return node;
            }
            if (node.children) {
                const found = findNodeById(node.children, nodeId);
                if (found)
                    return found;
            }
        }
        return null;
    };
    // Get the selected node's tooltipPath
    const transformedTreeData = projectTreeData
        ? transformProjectTreeData(projectTreeData)
        : [];
    const selectedNode = activeNodeId
        ? findNodeById(transformedTreeData, activeNodeId)
        : null;
    const selectedTooltipPath = (selectedNode === null || selectedNode === void 0 ? void 0 : selectedNode.tooltipPath) || "";
    // Auto-expand all nodes and select first node when tree data loads
    useEffect(() => {
        if (projectTreeData && projectTreeData.length > 0) {
            const transformedData = transformProjectTreeData(projectTreeData);
            // Auto-expand all expandable nodes
            const allExpandableNodes = new Set();
            const collectExpandableNodes = (nodes) => {
                nodes.forEach((node) => {
                    if (node.isExpandable) {
                        allExpandableNodes.add(node.id);
                        if (node.children) {
                            collectExpandableNodes(node.children);
                        }
                    }
                });
            };
            collectExpandableNodes(transformedData);
            setExpandedNodes(allExpandableNodes);
            // Auto-select the first node (root node) if no node is currently selected
            if (!activeNodeId && transformedData.length > 0) {
                const firstNode = transformedData[0];
                setActiveNodeId(firstNode.id);
                setSelectedChildProject(firstNode.id);
            }
        }
    }, [projectTreeData, activeNodeId]);
    return (React.createElement(Box, null, isLoadingPermissions ? (React.createElement(Box, { sx: { p: 4, textAlign: "center" } },
        React.createElement(CircularProgress, null))) : noAccess ? (React.createElement(Box, { sx: { p: 4, textAlign: "center" } },
        React.createElement(Typography, { variant: "h6", color: "error" }, "U heeft geen toegang tot de projecten module"))) : (React.createElement(React.Fragment, null,
        React.createElement(PageHeader, { title: "Projectgegevens", subtitle: selectedTooltipPath || undefined, onBack: () => navigate("/project"), actions: React.createElement(React.Fragment, null,
                isMobile ? (React.createElement(Box, { display: "flex", alignItems: "center", gap: 1 },
                    React.createElement(IconButton, { color: "primary", sx: {
                            backgroundColor: theme.palette.tertiary[40],
                            color: "white",
                            "&:hover": {
                                backgroundColor: "primary.dark",
                            },
                        }, onClick: handleMenuOpen },
                        React.createElement(Edit2, { variant: "Bulk" })))) : (React.createElement(Box, { display: "flex", alignItems: "center", gap: 1.5 },
                    React.createElement(Button, { variant: "outlined", color: "tertiary", size: "medium", disabled: pocButtonDisabled, onClick: () => setOpenForecastDialog(true) }, "POC aanpassen"),
                    React.createElement(Button, { variant: "outlined", color: "tertiary", size: "medium", disabled: saveButtonsDisabled, onClick: () => {
                            const params = new URLSearchParams(searchParams);
                            params.set("childProjectNumber", selectedChildProject || topProjectNumber);
                            navigate(`/project/${projectId}/details/forecast?${params.toString()}`, {
                                state: {
                                    projectName: selectedTooltipPath ||
                                        `${topProjectNumber}: ${entityName}`,
                                },
                            });
                        } }, "Forecast aanpassen"),
                    React.createElement(Button, { variant: "contained", color: "tertiary", size: "medium", disabled: saveButtonsDisabled || isSaving, onClick: handleSaveMetrics, startIcon: isSaving ? (React.createElement(CircularProgress, { size: 16, color: "inherit" })) : null }, isSaving ? "Opslaan..." : "Cijfers opslaan"))),
                React.createElement(FMenu, { anchorEl: menuAnchor, open: Boolean(menuAnchor), handleClose: handleMenuClose, menuItems: menuItems })) }),
        React.createElement(Grid, { container: true, spacing: 2 },
            React.createElement(Grid, { size: { xs: 12, sm: 6, md: 3 } },
                React.createElement(Box, { sx: {
                        maxHeight: {
                            xs: "calc(100vh - 300px)",
                            sm: "calc(100vh - 220px)",
                        },
                        background: theme.palette.background.neutral,
                        borderRadius: "24px",
                        backdropFilter: "blur(40px)",
                        overflow: "auto",
                    } }, !topProjectNumber || !entityName ? (React.createElement(Box, { sx: { p: 2, textAlign: "center", color: "text.secondary" } }, "Ontbrekende projectparameters. Navigeer vanaf de projectlijst.")) : isTreeLoading ? (React.createElement(Box, { sx: { p: 2 } },
                    React.createElement(Box, { sx: { px: 2, pt: 2, mb: 2 } },
                        React.createElement(Skeleton, { variant: "text", width: 140, height: 32 })),
                    [...Array(isMobile ? 1 : 8)].map((_, index) => (React.createElement(Box, { key: index, sx: {
                            pl: index % 3 === 0 ? 2 : index % 3 === 1 ? 4 : 6,
                            pr: 2,
                            mb: 1,
                        } },
                        React.createElement(Skeleton, { variant: "rectangular", height: 40, sx: { borderRadius: 1 } })))))) : treeError ? (React.createElement(Box, { sx: { p: 2, textAlign: "center", color: "error.main" } }, "Fout bij het laden van projectboom")) : (React.createElement(React.Fragment, null,
                    React.createElement(Box, { sx: {
                            px: 2,
                            pt: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        } },
                        React.createElement(Typography, { variant: "h6", fontWeight: 500, sx: { color: theme.palette.tertiary[30] } }, "Projectboom"),
                        React.createElement(Icon, { icon: "hugeicons:information-circle", width: "18", height: "18", color: theme.palette.info.main })),
                    React.createElement(TreeMenuNew, { data: projectTreeData
                            ? transformProjectTreeData(projectTreeData)
                            : [], expandedNodes: expandedNodes, activeNodeId: activeNodeId, onToggle: handleToggle, onNodeClick: handleNodeClick, isMobile: isMobile ? true : false, showOnlyFirst: isMobile ? true : false, hideChildren: isMobile ? true : false, onMaximizeClick: () => setTreeDrawerOpen(true) }))))),
            isMobile && (React.createElement(React.Fragment, null,
                React.createElement(FDrawer, { open: treeDrawerOpen, onClose: () => setTreeDrawerOpen(false), header: React.createElement(Box, { sx: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        } },
                        React.createElement(Typography, { variant: "h6" }, "Projectboom"),
                        React.createElement(Icon, { icon: "hugeicons:information-circle", width: "18", height: "18", color: theme.palette.info.main })) },
                    React.createElement(Box, { sx: {
                            background: theme.palette.background.neutral,
                            borderRadius: "24px",
                            p: 2,
                        } }, !topProjectNumber || !entityName ? (React.createElement(Box, { sx: {
                            p: 2,
                            textAlign: "center",
                            color: "text.secondary",
                        } }, "Ontbrekende projectparameters. Navigeer vanaf de projectlijst.")) : isTreeLoading ? (React.createElement(Box, { sx: { p: 2 } },
                        React.createElement(Box, { sx: { mb: 2 } },
                            React.createElement(Skeleton, { variant: "text", width: 140, height: 32 })),
                        [...Array(isMobile ? 1 : 8)].map((_, index) => (React.createElement(Box, { key: index, sx: {
                                pl: index % 3 === 0 ? 0 : index % 3 === 1 ? 2 : 4,
                                mb: 1,
                            } },
                            React.createElement(Skeleton, { variant: "rectangular", height: 40, sx: { borderRadius: 1 } })))))) : treeError ? (React.createElement(Box, { sx: { p: 2, textAlign: "center", color: "error.main" } }, "Error loading project tree")) : (React.createElement(TreeMenuNew, { data: projectTreeData
                            ? transformProjectTreeData(projectTreeData)
                            : [], expandedNodes: expandedNodes, activeNodeId: activeNodeId, onToggle: handleToggle, onNodeClick: (nodeId) => {
                            handleNodeClick(nodeId);
                            setTreeDrawerOpen(false);
                        } })))))),
            React.createElement(Grid, { size: { xs: 12, sm: 12, md: 9 }, height: isMobile ? "calc(100dvh - 327px)" : "calc(100dvh - 190px)", sx: { overflow: "auto", pb: 1, pr: 1 } }, isTreeLoading ? (React.createElement(Box, { sx: { p: 2 } },
                React.createElement(Box, { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 3, sx: { mb: 3 } },
                    React.createElement(Skeleton, { variant: "text", width: 250, height: 40 }),
                    React.createElement(Skeleton, { variant: "rectangular", width: 140, height: 40, sx: { borderRadius: 1 } })),
                React.createElement(Box, { sx: Object.assign({}, stack2) }, [...Array(1)].map((_, index) => (React.createElement(Skeleton, { key: index, variant: "rectangular", height: 32, sx: { borderRadius: 2 } })))),
                React.createElement(Box, { sx: Object.assign({}, stack2) },
                    React.createElement(Skeleton, { variant: "text", width: 220, height: 32, sx: { mb: 2 } }),
                    React.createElement(Skeleton, { variant: "rectangular", height: 400, sx: { borderRadius: 2, mb: 3 } }),
                    React.createElement(Skeleton, { variant: "text", width: 240, height: 32, sx: { mb: 2 } }),
                    React.createElement(Skeleton, { variant: "rectangular", height: 400, sx: { borderRadius: 2 } })))) : (React.createElement(React.Fragment, null,
                React.createElement(Box, { display: "flex", flexDirection: "column", gap: 1.5, sx: { pt: 2 } },
                    React.createElement(Box, { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 3 },
                        React.createElement(Typography, { variant: "h5", fontWeight: 500 }, (selectedNode === null || selectedNode === void 0 ? void 0 : selectedNode.label) || topProjectNumber),
                        React.createElement(FormControl, { fullWidth: isMobile ? true : false, sx: {
                                minWidth: 140,
                                marginLeft: "auto",
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: "background.paper",
                                    "&:hover": {
                                        backgroundColor: "action.hover",
                                    },
                                },
                            } },
                            React.createElement(InputLabel, { id: "unit-select-label", sx: {
                                    fontSize: "0.875rem",
                                } }, "Eenheid"),
                            React.createElement(Select, { labelId: "unit-select-label", id: "unit-select", value: unitType, label: "Eenheid", onChange: (e) => setUnitType(e.target.value) },
                                React.createElement(MenuItem, { value: "K", sx: { fontSize: "0.875rem" } }, "K (Duizend)"),
                                React.createElement(MenuItem, { value: "full", sx: { fontSize: "0.875rem" } }, "Volledig")))),
                    showInfoAlert && (React.createElement(Alert, { variant: "standard", sx: {
                            borderRadius: 3,
                            display: "flex",
                            alignItems: "center",
                            "& .MuiAlert-icon": {
                                display: "flex",
                                alignItems: "center",
                            },
                            "& .MuiAlert-message": {
                                display: "flex",
                                alignItems: "center",
                                flex: 1,
                            },
                            "& .MuiAlert-action": {
                                display: "flex",
                                alignItems: "center",
                                paddingTop: 0,
                                paddingRight: 0,
                            },
                        }, severity: "info", icon: React.createElement(InfoCircle, { variant: "Bulk", color: theme.palette.info.main, size: 24 }), 
                        // icon={<Icon icon="hugeicons:fill-information-circle" width="18" height="18" color={theme.palette.info.main} />}
                        action: React.createElement(IconButton, { size: "small", onClick: () => setShowInfoAlert(false), sx: {
                                color: theme.palette.info.darker,
                                border: "none",
                                "&:hover": {
                                    backgroundColor: "transparent",
                                },
                            } },
                            React.createElement(Icon, { icon: "mingcute:close-line", width: "18", height: "18" })) }, "Deze inzichten omvatten gegevens van alle subprojecten die aan dit project zijn gerelateerd.")),
                    React.createElement(Box, { sx: {
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                lg: "repeat(3, 1fr)",
                            },
                            gap: 1.5,
                        } }),
                    React.createElement(ProjectTable, { parentProjectNumber: topProjectNumber, childProjectNumber: selectedChildProject || topProjectNumber, projectName: selectedTooltipPath || undefined, unitType: unitType })))))),
        React.createElement(ForcastAdjustDialog, { open: openForecastDialog, onClose: () => {
                setOpenForecastDialog(false);
                // Refetch tree and metrics when dialog closes
                if (refetchTree) {
                    refetchTree();
                }
                if (refetchMetrics) {
                    refetchMetrics();
                }
            }, parentProjectNumber: topProjectNumber, childProjectNumber: selectedChildProject || topProjectNumber, projectId: projectId, companyId: companyId, isSaveDisabled: pocButtonDisabled }),
        React.createElement(ProjectTableDrawer, { open: drawerOpen, onClose: handleDrawerClose, activeNodeId: activeNodeId, isMobile: isMobile, parentProjectNumber: topProjectNumber, childProjectNumber: selectedChildProject || topProjectNumber, projectName: selectedTooltipPath || undefined, unitType: unitType })))));
};
